import React, { useState } from 'react';
import { X, Link2, Check } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string | null | undefined;
  title?: string;
  description?: string;
  clipId?: string; // For generating shareable in-app link
  children?: React.ReactNode;
}

export const VideoModal: React.FC<VideoModalProps> = ({
  isOpen,
  onClose,
  url,
  title,
  description,
  clipId,
  children,
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  // Generate the in-app shareable link
  const getShareableLink = () => {
    if (!clipId) return null;
    const baseUrl = window.location.origin;
    return `${baseUrl}/clip/${clipId}`;
  };

  const copyShareLink = async () => {
    const link = getShareableLink();
    if (link) {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareableLink = getShareableLink();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-w-4xl w-full">
        <div className="flex justify-between items-center mb-2">
          {/* Share button */}
          {shareableLink && (
            <button
              onClick={copyShareLink}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                copied
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
              }`}
            >
              {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
              {copied ? 'Link Copied!' : 'Copy Link'}
            </button>
          )}
          {!shareableLink && <div />}

          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <VideoPlayer url={url} className="mb-4" />

        {(title || description) && (
          <div className="text-center mb-4">
            {title && <h3 className="text-xl font-bold text-white mb-1">{title}</h3>}
            {description && <p className="text-zinc-400">{description}</p>}
          </div>
        )}

        {children}
      </div>
    </div>
  );
};
